import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Service for managing the PWA app icon badge using the Badge API.
 *
 * The Badge API allows installed PWAs to display notification counts
 * on the application icon.
 *
 * Browser Support:
 * - Chrome/Edge 81+ (Windows, macOS)
 * - Safari 16.4+ (iOS/iPadOS)
 * - Not supported: Firefox, Android, Linux (API exists but badge not shown)
 *
 * Note: Badge API only works when the PWA is installed on the device.
 * On unsupported browsers or web contexts, operations silently fail.
 */
@Injectable({
  providedIn: 'root'
})
export class BadgeService {
  private isSupported: boolean;
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isSupported = this.checkSupport();

    if (this.isSupported) {
      console.log('[BadgeService] Badge API is supported and available');
    } else if (this.isBrowser) {
      console.log('[BadgeService] Badge API is not supported in this browser');
    }
  }

  /**
   * Checks if the Badge API is supported in the current environment.
   * @returns true if Badge API is available, false otherwise
   */
  private checkSupport(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    return 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
  }

  /**
   * Sets the badge on the app icon to display the specified count.
   * If count is 0, the badge is cleared.
   *
   * @param count - The number to display on the badge (0 to clear)
   * @returns Promise that resolves when the badge is set, or immediately if not supported
   */
  async setBadge(count: number): Promise<void> {
    if (!this.isSupported) {
      return;
    }

    try {
      if (count > 0) {
        await navigator.setAppBadge!(count);
      } else {
        await this.clearBadge();
      }
    } catch (error) {
    }
  }

  /**
   * Clears the badge from the app icon.
   * @returns Promise that resolves when the badge is cleared, or immediately if not supported
   */
  async clearBadge(): Promise<void> {
    if (!this.isSupported) {
      return;
    }

    try {
      await navigator.clearAppBadge!();
    } catch (error) {
      console.warn('[BadgeService] Failed to clear app badge:', error);
    }
  }

  /**
   * Returns whether the Badge API is supported in the current environment.
   * @returns true if supported, false otherwise
   */
  isBadgeSupported(): boolean {
    return this.isSupported;
  }
}
